import { useState, useEffect, useCallback } from 'react';
import { s, authHeaders, Toast } from './listPageStyles';
import { ListPageShell } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import './securityScreens.css';

// G-L-05 — Aktif oturumlar. Liste OData DEĞİL: /api/auth/sessions kullanıcı bazlı çalışır ve
// ApiResponse zarfı döner, bu yüzden c360 (useC360List) altyapısı burada kullanılmaz.
//
// KK-14 bağlamı: yetki değişikliği oturumu KESMEZ; yeni yetki kullanıcı çıkış/giriş yapınca
// geçerli olur. Acil durumda oturum bu ekrandan elle sonlandırılır.

const PLATFORMS = { 1: 'Web', 2: 'Android', 3: 'iOS', 4: 'Servis' };

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('tr-TR') : '-');

/** "3 saat önce" gibi göreli süre — oturumun canlılığı mutlak tarihten daha okunaklı. */
function relative(v) {
  if (!v) return '-';
  const diffMs = Date.now() - new Date(v).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'az önce';
  if (min < 60) return `${min} dk önce`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour} saat önce`;
  return `${Math.floor(hour / 24)} gün önce`;
}

export function SessionsPage() {
  usePageMeta('Aktif Oturumlar', 'Güvenlik & İzleme');
  const { canDelete } = usePageActions();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState('');       // boş = kendi oturumlarım
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = userId ? `?userId=${encodeURIComponent(userId)}` : '';
      const res = await fetch(`/api/auth/sessions${qs}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Oturumlar okunamadı');
      setRows(body?.data || body?.Data || []);
    } catch (e) {
      showToast('error', e.message || 'Oturumlar okunamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const revoke = async () => {
    if (!confirm) return;
    try {
      const res = await fetch(`/api/auth/sessions/${confirm.id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Oturum sonlandırılamadı');
      showToast('success', 'Oturum sonlandırıldı');
      setConfirm(null);
      load();
    } catch (e) {
      showToast('error', e.message || 'Oturum sonlandırılamadı');
      setConfirm(null);
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="number"
        placeholder="Kullanıcı Id (boş = kendi oturumlarım)"
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
      />
      {userId && <button style={s.btnSecondary} onClick={() => setUserId('')}>Temizle</button>}
      <button style={s.btnSecondary} onClick={load}>Yenile</button>
    </>
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Aktif Oturumlar"
        subtitle="Açık oturumlar ve bağlandıkları cihazlar. Bir oturumu sonlandırmak kullanıcıyı o cihazda çıkışa zorlar."
        toolbar={toolbarNode}
      >
        <div className="sec-cards">
          <div className="sec-card">
            <div className="sec-card-label">Açık oturum</div>
            <div className="sec-card-value">{loading ? '…' : rows.length}</div>
            <div className="sec-card-hint">{userId ? `Kullanıcı #${userId}` : 'Kendi oturumlarınız'}</div>
          </div>
        </div>

        <table className="c360-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Platform</th><th>Cihaz</th><th>Sürüm</th><th>IP</th>
              <th>Açılış</th><th>Son hareket</th><th>Bitiş</th><th>İşlem</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24 }}>Yükleniyor…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24 }}>Açık oturum bulunamadı</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id}>
                <td><span className="sec-badge sec-badge--info">{PLATFORMS[r.platform] || '-'}</span></td>
                <td>{r.deviceId ? <span className="c360-erp-chip">{String(r.deviceId).slice(0, 8)}…</span> : '-'}</td>
                <td>{r.clientVersion || '-'}</td>
                <td>{r.ipAddress || '-'}</td>
                <td>{fmtDateTime(r.issuedAt)}</td>
                <td title={fmtDateTime(r.lastSeenAt)}>{relative(r.lastSeenAt || r.issuedAt)}</td>
                <td>{fmtDateTime(r.expiresAt)}</td>
                <td>
                  {canDelete && (
                    <button type="button" style={s.btnDanger} onClick={() => setConfirm(r)}>Sonlandır</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ListPageShell>

      {confirm && (
        <div style={s.overlay} onClick={() => setConfirm(null)}>
          <div style={{ ...s.modal, maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Oturumu Sonlandır</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>{PLATFORMS[confirm.platform] || 'Bilinmeyen'}</strong> oturumu sonlandırılacak.
                Kullanıcı o cihazda yeniden giriş yapmak zorunda kalır.
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={revoke}>Evet, Sonlandır</button>
              <button style={s.btnSecondary} onClick={() => setConfirm(null)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default SessionsPage;
